import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useGetActiveCheckinDefinition } from "../../../api/queries";
import { useCreateUserToolboxCheckin } from "../../../api/mutations";
import { useNotification } from "../../../hooks/useNotification";
import Button from "../../../componentLibrary/Button";
import RatingScale from "../../../componentLibrary/RatingScale";
import Spinner from "../../../componentLibrary/Spinner";
import type { ToolboxAnswers, ToolboxAnswerValue } from "../../../types";
import { renderToolboxQuestion } from "../components/ToolboxQuestion";

export default function ToolboxCheckinPage() {
  const { topicId } = useParams<{ topicId: string }>();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const [rating, setRating] = useState<number | null>(null);
  const [answers, setAnswers] = useState<ToolboxAnswers>({});

  const {
    data: definition,
    isLoading,
    isError,
  } = useGetActiveCheckinDefinition(topicId ?? "");

  const { mutate: createCheckin, isPending: isSaving } =
    useCreateUserToolboxCheckin({
      onSuccess: async () => {
        notify("success", "Check-in saved.");
        navigate(`/toolboxes/${topicId}`);
      },
      onError: () => {
        notify("error", "Failed to save check-in. Please try again.");
      },
    });

  const handleChange = (variableName: string, value: ToolboxAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [variableName]: value }));
  };

  const handleSubmit = () => {
    if (!topicId) return;
    if (definition?.ratingEnabled && rating === null) {
      notify("error", "Please select a rating before submitting.");
      return;
    }
    createCheckin({
      topicId,
      rating: definition?.ratingEnabled ? (rating ?? undefined) : undefined,
      answers,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (isError || !definition) {
    return (
      <div className="space-y-4 pt-4">
        <Link
          to={`/toolboxes/${topicId}`}
          relative="path"
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors mb-4 w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to toolbox
        </Link>
        <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-16">
          <p className="text-sm text-gray-400">
            No check-in questions have been set up for this topic yet.
          </p>
        </div>
      </div>
    );
  }

  const ratingScale = definition.ratingScale ?? 5;

  return (
    <div className="pt-4">
      <Link
        to={`/toolboxes/${topicId}`}
        relative="path"
        className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors mb-4 w-fit"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to toolbox
      </Link>

      <h1 className="text-2xl font-bold text-gray-800 mb-6">New Check-In</h1>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-8 items-start">
        {/* Main form column */}
        <div className="space-y-6">
          {definition.ratingEnabled && (
            <RatingScale
              value={rating}
              onChange={setRating}
              scale={ratingScale}
              label={definition.ratingLabel || "How are you feeling?"}
              lowLabel={definition.ratingMinLabel}
              highLabel={definition.ratingMaxLabel}
            />
          )}

          {definition.questions.length > 0 && (
            <div className="space-y-6">
              {definition.questions.map((question) =>
                renderToolboxQuestion(question, answers, handleChange),
              )}
            </div>
          )}

          {/* Submit button on mobile */}
          <div className="pt-2 pb-8 md:hidden">
            <Button
              type="button"
              loading={isSaving}
              onClick={handleSubmit}
              className="w-full"
            >
              Submit Check-In
            </Button>
          </div>
        </div>

        {/* Sticky sidebar on desktop */}
        <div className="hidden md:block sticky top-6">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Ready to submit?
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Your responses are saved privately and used to track your
                progress over time.
              </p>
            </div>
            <Button
              type="button"
              loading={isSaving}
              onClick={handleSubmit}
              className="w-full"
            >
              Submit Check-In
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
